// src/features/media/components/MediaToolbar.tsx

import { Group, TextInput, Switch, Text, Button, ActionIcon, Badge } from '@mantine/core';
import {
  IconSearch,
  IconTrash,
  IconPlayerPlay,
  IconPlayerPause,
  IconArrowMerge,
  IconFilter,
  IconX,
} from '@tabler/icons-react';
import { useSelection } from '@/shared/contexts/SelectionContext';
import type { SearchMode } from '@/lib/types/Media';
import styles from './MediaToolbar.module.css';

interface MediaToolbarProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  filterCount: number;
  filtersOpen: boolean;
  onToggleFilters: () => void;
  totalCount: number;
  resultCount: number;
  onBulkActivate: () => void;
  onBulkDeactivate: () => void;
  onBulkDelete: () => void;
  onMerge: () => void;
  activateLoading?: boolean;
  deactivateLoading?: boolean;
  deleteLoading?: boolean;
}

export function MediaToolbar({
  searchText,
  onSearchChange,
  searchMode,
  onSearchModeChange,
  filterCount,
  filtersOpen,
  onToggleFilters,
  totalCount,
  resultCount,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
  onMerge,
  activateLoading,
  deactivateLoading,
  deleteLoading,
}: MediaToolbarProps) {
  const { selectedCount, clearSelection } = useSelection();

  const hasSelection = selectedCount > 0;
  const isFiltered = searchText.trim().length > 0 || filterCount > 0;

  return (
    <Group justify="space-between" className={styles.toolbar}>
      <Group>
        <TextInput
          placeholder="Search title, tags, description, ref id..."
          leftSection={<IconSearch size={16} />}
          value={searchText}
          onChange={e => onSearchChange(e.currentTarget.value)}
          className={styles.searchInput}
        />

        <Switch
          label="Exact match"
          checked={searchMode === 'exact'}
          onChange={(e) => onSearchModeChange(e.currentTarget.checked ? 'exact' : 'fuzzy')}
          size="sm"
        />

        <Button
          variant={filtersOpen || filterCount > 0 ? 'light' : 'default'}
          leftSection={<IconFilter size={16} />}
          rightSection={
            filterCount > 0 ? (
              <Badge size="sm" variant="filled" circle>
                {filterCount}
              </Badge>
            ) : undefined
          }
          onClick={onToggleFilters}
        >
          Filters
        </Button>

        <Text size="sm" c="dimmed">
          {isFiltered
            ? `${resultCount} of ${totalCount} videos`
            : `${totalCount} videos`
          }
        </Text>
      </Group>

      {hasSelection && (
        <Group gap="xs">
          <Text size="sm" fw={500}>
            {selectedCount} selected
          </Text>

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={onBulkActivate}
            loading={activateLoading}
          >
            Activate
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPause size={14} />}
            onClick={onBulkDeactivate}
            loading={deactivateLoading}
          >
            Deactivate
          </Button>
          {selectedCount >= 2 && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconArrowMerge size={14} />}
              onClick={onMerge}
            >
              Merge
            </Button>
          )}
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={onBulkDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={clearSelection}
            aria-label="Clear selection"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      )}
    </Group>
  );
}
